/**
 * +tutorial: the guided first run's app glue (docs/design/tutorial.md).
 *  - offerOnFirstLaunch(): on a fresh profile with no tutorial flag, "pick your hero" → tutorial;
 *  - start(classId): a run in the tutorial yard (base kit, no gear / talents; bots fill the rest),
 *    driven by tutorial/run.ts. Never touches the profile: no rewards, no rating, no run record;
 *  - at the end, a "Training complete" sheet over the lobby with the rotation score; skipping
 *    goes straight back to the lobby. Both set the flag in tutorial/prefs.ts.
 * main.ts lends its run plumbing (renderer factory, session slot, menu) through `TutorialAppDeps`.
 */
import type { ClassId, ContentRegistry } from '@keystone/sim';
import { createRegistry } from '@keystone/sim';
import { DEFAULT_PARTY, TUTORIAL, tutorialBundle } from '@keystone/content';
import { type Session, type SessionEnd, startSession } from '../game/session';
import type { StartConfig } from '../hud/screens';
import type { GraphicsSettings, RendererKind } from '../render/settings';
import type { Renderer } from '../render/types';
import { heroFor } from '../meta/heroes';
import { el } from '../meta/ui/dom';
import { classCrest, ghostButton, kmModal, kmToast, primaryButton, secondaryButton } from '../meta/kmmo';
import { tutorialPrefs, shouldOfferTutorial } from './prefs';
import { type TutorialOutcome, tutorialRun } from './run';
import { TUTORIAL_CLASSES } from './scripts';
import './tutorial.css';

export interface TutorialAppDeps {
  canvasHost: HTMLElement;
  hudHost: HTMLElement;
  /** The base content registry (uncompiled). */
  content: ContentRegistry;
  config(): StartConfig;
  settings(): GraphicsSettings;
  rendererFactory(kind: RendererKind, registry: ContentRegistry): Promise<(host: HTMLElement) => Renderer>;
  /** Tear down whatever runs and take the screen. Returns "is this still the current launch?". */
  begin(): () => boolean;
  /** The tutorial session started (main keeps it for the settings panel and menu()). */
  started(s: Session): void;
  /** Back to the lobby (disposes the session). */
  menu(): void;
  mainClassId(): ClassId;
  profileRuns(): number;
  /** Make `classId` the player's hero (the first-launch pick). */
  setMainClass(classId: ClassId): void;
}

export interface TutorialApp {
  start(classId?: ClassId): void;
  /** First launch: offer the tutorial (class pick first). True when it was shown. */
  offerOnFirstLaunch(): boolean;
  /** The "pick a hero, then train" sheet (also used by Replay from the menus). */
  pick(): void;
}

const PITCH: Readonly<Record<string, string>> = {
  warden: 'Hold every enemy’s attention behind a shield. Build Bastion, cash it in to heal.',
  lightbinder: 'Keep the party alive. Build Holy Power, spend it on big heals, burst with Dawnbringer.',
  blademaster: 'Stack Wounds up close, then cash them in with Mortal Strike and Bladestorm.',
  stormcaller: 'Build Maelstrom with lightning from range, spend it on Earth Shock.',
};

export function createTutorialApp(d: TutorialAppDeps): TutorialApp {
  let launching = 0;

  async function start(classId: ClassId = d.mainClassId()): Promise<void> {
    const seq = ++launching;
    const current = d.begin();
    const cls = TUTORIAL_CLASSES.includes(classId) ? classId : 'blademaster';
    const registry = createRegistry(tutorialBundle(d.content.bundle));
    const party = [...DEFAULT_PARTY] as ClassId[];
    const slot = Math.max(0, party.indexOf(cls));
    let factory: (host: HTMLElement) => Renderer;
    try {
      factory = await d.rendererFactory(d.settings().renderer, registry);
    } catch (err) {
      console.error('[tutorial] renderer failed', err);
      d.menu();
      return;
    }
    if (!current() || seq !== launching) return;
    const run = tutorialRun(cls, registry, {
      onSkip: () => {
        const p = tutorialPrefs();
        if (p.get().status !== 'done') p.set({ status: 'skipped' });
        d.menu();
        kmToast('Tutorial skipped. Replay it anytime from Dungeons or ⚙ Settings.', 'info', 4500);
      },
    });
    const config: StartConfig = { ...d.config(), slot, level: 2, affixes: [], dungeonId: TUTORIAL.dungeon, seed: 4242 };
    const onEnd = (_data: SessionEnd): void => {
      const out = run.outcome();
      const prefs = tutorialPrefs();
      const best = { ...prefs.get().best };
      if (out.score) best[cls] = Math.max(best[cls] ?? 0, out.score.score);
      prefs.set({ status: 'done', best });
      d.menu();
      summarySheet(out, () => pickSheet());
    };
    try {
      const s = startSession({
        canvasHost: d.canvasHost,
        hudHost: d.hudHost,
        content: registry,
        party,
        partyNames: party.map((c) => heroFor(c).name),
        progress: null,
        config,
        onEnd,
        createRenderer: factory,
        settings: d.settings(),
        hooks: run.hooks,
        personas: null, // +personas: the guided tutorial's bots stay quiet
      });
      d.started(s);
      // Debug handle (dev builds only): the tutorial session and its director.
      if (import.meta.env.DEV) (globalThis as { __tutorialRun?: unknown }).__tutorialRun = { session: s, run };
    } catch (err) {
      console.error('[tutorial] could not start', err);
      kmToast('Could not start the tutorial (see console).', 'error', 5000);
      d.menu();
    }
  }

  function pickSheet(first = false): void {
    const m = kmModal(first ? 'Welcome to Keystone' : 'Training Grounds', { wide: true });
    m.el.classList.add('tut-pick');
    el('p', 'km-modal-text', m.body, first ? 'Pick your hero. A short guided run in the Training Grounds teaches the controls and every one of your skills. The other three heroes join you as bot companions.' : 'Replay the guided run with any hero. It takes about five minutes and grants no rewards.');
    let chosen: ClassId = d.mainClassId();
    const row = el('div', 'tut-pick-row', m.body);
    const cards: { id: string; el: HTMLElement }[] = [];
    const sync = (): void => {
      for (const c of cards) c.el.classList.toggle('selected', c.id === chosen);
      startBtn.textContent = `Train as ${heroFor(chosen).name}`;
    };
    for (const id of TUTORIAL_CLASSES) {
      const h = heroFor(id);
      const card = el('button', 'tut-pick-card', row);
      card.type = 'button';
      classCrest(id, { size: 'md' }, card);
      el('div', 'tut-pick-name', card, h.name);
      el('div', 'tut-pick-role', card, h.roleLabel);
      el('div', 'tut-pick-pitch', card, PITCH[id] ?? '');
      const best = tutorialPrefs().get().best[id];
      if (best !== undefined) el('div', 'tut-pick-best', card, `Best rotation score ${best}`);
      card.addEventListener('click', () => {
        chosen = id;
        sync();
      });
      cards.push({ id, el: card });
    }
    ghostButton('Skip tutorial', {
      onClick: () => {
        m.close();
        const p = tutorialPrefs();
        if (p.get().status === null) p.set({ status: 'skipped' });
        if (first) kmToast('You can play the tutorial anytime from Dungeons or ⚙ Settings.', 'info', 4500);
      },
    }, m.footer);
    const startBtn = primaryButton('Train', {
      size: 'lg',
      onClick: () => {
        m.close();
        // The first-launch pick makes it your hero; a replay just trains that class.
        if (first && chosen !== d.mainClassId()) d.setMainClass(chosen);
        void start(chosen);
      },
    }, m.footer);
    sync();
  }

  return {
    start: (c) => void start(c),
    pick: () => pickSheet(false),
    offerOnFirstLaunch() {
      if (!shouldOfferTutorial(tutorialPrefs().get(), d.profileRuns())) return false;
      pickSheet(true);
      return true;
    },
  };
}

/** "Training complete" over the lobby: rotation score, a few stats, one tip. */
function summarySheet(out: TutorialOutcome, replay: () => void): void {
  const m = kmModal(out.graduated ? 'Training complete' : 'Training over', { wide: true });
  m.el.classList.add('tut-summary');
  const top = el('div', 'tut-sum-top', m.body);
  const sc = out.score;
  const grade = el('div', `tut-sum-grade g-${sc?.grade ?? 'none'}`, top);
  el('span', '', grade, sc?.grade ?? '–');
  grade.title = 'Rotation grade';
  const tt = el('div', 'tut-sum-score', top);
  el('div', 'tut-sum-label', tt, 'Rotation score');
  el('div', 'tut-sum-num km-num', tt, sc ? String(sc.score) : '–');
  el('div', 'tut-sum-sub', tt, `Graduation pull as ${heroFor(out.classId).name}`);

  const rows = el('div', 'tut-sum-rows', m.body);
  const row = (label: string, value: string, fill: number | null): void => {
    const r = el('div', 'tut-sum-row', rows);
    el('span', 'tut-sum-row-label', r, label);
    const bar = el('span', 'tut-sum-bar', r);
    if (fill !== null) el('i', '', bar).style.width = `${Math.round(Math.max(0, Math.min(1, fill)) * 100)}%`;
    el('b', 'km-num', r, value);
  };
  if (sc) {
    row('Always casting', `${Math.round(sc.alwaysCasting * 100)}%`, sc.alwaysCasting);
    row('Procs used', sc.procsGained ? `${sc.procsGained - sc.procsWasted}/${sc.procsGained}` : '–', sc.procsGained ? 1 - sc.procsWasted / sc.procsGained : null);
    row('Transforms used', sc.windows ? `${sc.windowsUsed}/${sc.windows}` : '–', sc.windows ? sc.windowsUsed / sc.windows : null);
  }
  const s = out.stats;
  const chips = el('div', 'tut-sum-chips', m.body);
  const chip = (t: string): void => void el('span', 'tut-sum-chip', chips, t);
  if (out.classId === 'lightbinder') chip(`Cleanses ${s.dispels}`);
  else chip(`Kicks ${s.kicks}`);
  chip(`Dodged ${s.dodges}`);
  chip(`Hit by orange ${s.telegraphHits}`);
  chip(`Procs spent ${s.procsUsed}`);

  const tip = !sc
    ? 'Finish the graduation pull to get a rotation score.'
    : sc.alwaysCasting < 0.8
      ? 'Tip: never stand idle. When nothing is lit, press your filler.'
      : sc.procsGained && sc.procsWasted / sc.procsGained > 0.2
        ? 'Tip: a marching gold border means “press me soon”. Spend procs before they fade.'
        : s.telegraphHits > 1
          ? 'Tip: orange on the ground hurts. Step out early, or tap Shift.'
          : 'Great rotation. You are ready for a real key.';
  el('p', 'km-modal-text tut-sum-tip', m.body, tip);
  el('p', 'km-modal-text tut-sum-note', m.body, 'For your next runs, the Run Rewards screen adds a coaching tip or two.');
  secondaryButton('Replay tutorial', { onClick: () => (m.close(), replay()) }, m.footer);
  primaryButton('Continue', { size: 'lg', onClick: () => m.close() }, m.footer);
}
